import { useState, useEffect, useRef } from 'react';

/**
 * useCountUp — animates a number from 0 (or its previous value) to `target`.
 * Uses requestAnimationFrame for a silky-smooth ease-out-cubic curve.
 *
 * @param {number} target   - The value to animate to
 * @param {number} duration - Animation duration in ms (default 700)
 */
export function useCountUp(target, duration = 700) {
  const [display, setDisplay] = useState(target);
  const prevRef  = useRef(target);
  const rafRef   = useRef(null);

  useEffect(() => {
    const from = prevRef.current;
    const to   = target;
    prevRef.current = target;

    if (from === to) return;

    const start = performance.now();

    const step = (now) => {
      const elapsed  = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out-cubic
      const eased    = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);

  return display;
}
